import { useVote, UseVoteAPIResponseDTO, useWidget, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { getAppConfig } from '@telescope/cassini-utilities';
import { useAuth } from 'features/auth';
import { Open } from 'generated-types/widget';
import { keyBy } from 'lodash-es';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { PropertyName, VoteOption } from 'types';
import { useNextVoteWindow } from '../hooks/useNextVoteWindow';
import { AllocatedVote, bracketGridReducer, BracketGridState, BracketType } from '../reducers/bracketGridReducer';

const { isPreviewUrl } = getAppConfig();

export type BracketSize = 8 | 16 | 32;
export interface BracketGridContextProps extends BracketGridState {
  /**
   * Total selections the user has made which have not been submitted yet
   */
  totalAllocatedVotes: number;
  /**
   * A list of all vote options
   */
  voteOptions: VoteOption[];
  /**
   * Winner vote option
   */
  winner?: VoteOption | undefined;
  /**
   * Currently active round
   */
  activeRound: number;
  /**
   * Total rounds
   */
  totalRounds: number;
  /**
   * Total number of contestants in the bracket
   */
  totalContestants: BracketSize;
  /**
   * Total selections needed for the current round, based on the total rounds and the current round
   */
  totalVotesRequired: number;
  /**
   * current round to display in the viewport
   */
  displayedRound: number;
  /**
   * Returns true if the user has reached their vote limit for the current round
   */
  hasReachedVoteLimit: boolean;
  /**
   * Amount of times user can submit in the current round before reaching vote limit
   */
  votesRemaining: number;
  /**
   * Max number of bracket submissions for the current round.
   */
  voteLimit: number;
  /**
   * If true, a user attempted to submit a vote that was not successful (because they weren't logged in).
   * This is used to prevent allocated votes from being overwritten when a user logs in.
   */
  pendingBracketVote: boolean;
  pendingMatchupVote: VoteOption | null;
  /**
   * Update the round to display
   */
  setDisplayedRound: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Resets all allocated votes
   */
  resetVotes(): void;
  /**
   * Marks an option as allocated for the current round, or Removes it if it was already allocated.
   */
  toggleVote(voteOption: VoteOption): void;
  /**
   * Checks if the provided voteOption has been allocated
   */
  isVoteOptionSelected(voteOption: VoteOption): boolean;
  /**
   * Checks if the provided voteOption is active
   */
  isVoteOptionActive(voteOption: VoteOption): boolean;
  /**
   * Checks if the provided voteOption is voteable
   */
  isVoteOptionVoteable(voteOption: VoteOption): boolean;
  /**
   * Submit allocated votes for the entire bracket. This is used for the 'BY_ROUND' bracket type, where votes are submitted all at once.
   */
  submitVotes(): UseVoteAPIResponseDTO;
  /**
   * Submit a single vote. This is used for the 'BY_MATCHUP' bracket type, where votes are submitted one at a time rather than the entire bracket.
   */
  submitSingleVote(voteOption: VoteOption): UseVoteAPIResponseDTO;
  /**
   * If the user is not logged in, the vote submission needs to be deferred until the user is logged in. This will mark the submission as 'pending'
   */
  setPendingBracketVote: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingMatchupVote: React.Dispatch<React.SetStateAction<VoteOption | null>>;
}

export const BracketGridContext = createContext<BracketGridContextProps>(undefined!);
BracketGridContext.displayName = 'BracketGridContext';

export interface BracketGridProviderProps {
  children: React.ReactNode;
  activeRound: number;
  totalRounds: number;
  voteOptions: VoteOption[];
  winner?: VoteOption | undefined;
  voteLimit?: number;
  hasDailyVoteLimit?: boolean;
  bracketType: BracketType;
}

export function BracketGridProvider({
  children,
  activeRound,
  totalRounds,
  voteOptions,
  winner,
  voteLimit = Infinity,
  hasDailyVoteLimit = false,
  bracketType,
}: BracketGridProviderProps) {
  const { data: snapshotPropertyNames } = useWidget<Open>({
    select: (data) => keyBy(data.snapshot.property_names!, 'reference'),
  });
  const {
    data: { sid },
  } = useWidget<Open>({ select: (data) => data });
  const [displayedRound, setDisplayedRound] = useState(activeRound);
  const [pendingBracketVote, setPendingBracketVote] = useState(false);
  const [pendingMatchupVote, setPendingMatchupVote] = useState<VoteOption | null>(null);
  const vote = useVote();
  const { user, updateUserVoteHistory } = useAuth();
  const { total, ...previousVotes } = user?.votestring ?? { total: 0 };
  const [state, dispatch] = useReducer(bracketGridReducer, {
    allocatedVoteOptions: { ...(previousVotes as AllocatedVote) },
    isDirty: false,
    bracketType,
  });
  const userSID = user?.sid;
  const isByMatchup = bracketType === 'BY_MATCHUP';
  // Reset voting history on a new day, if the campaign has a daily vote limit (only applies to BY_ROUND)
  const onNextWindow = useCallback(() => {
    if (!user || isByMatchup || !hasDailyVoteLimit) return;

    resetVotes();

    if (user) {
      updateUserVoteHistory('');
    }
  }, [hasDailyVoteLimit, isByMatchup, updateUserVoteHistory, user]);

  useNextVoteWindow(onNextWindow);

  const propertyNames = useMemo(() => {
    return isPreviewUrl ? generateMockPropertyNames(voteOptions, activeRound) : snapshotPropertyNames;
  }, [voteOptions, snapshotPropertyNames, activeRound]);

  // clear allocated votes when SID changes
  useEffect(() => {
    if (userSID === sid) return;

    resetVotes();
    if (user) {
      updateUserVoteHistory('');
    }
  }, [sid, userSID, updateUserVoteHistory, user]);

  // Update the vote history when the user logs in
  useEffect(() => {
    if (user && user.votestring.total > 0 && user.sid === sid && !pendingBracketVote && !pendingMatchupVote) {
      const { total, ...previousVotes } = user?.votestring;
      dispatch({ type: 'SET_VOTES', payload: { ...(previousVotes as AllocatedVote) } });
    }
  }, [sid, user, pendingBracketVote, pendingMatchupVote]);

  /**
   * Checks if the vote already exists in the cache. If so removes it, otherwise it adds it.
   * If an entry already exists for the same cache key (i.e., user already voted for the other team, the previous vote is overwritten with the new vote)
   */
  const toggleVote = (vote: VoteOption) => {
    const propertyName = getRulesetPropertyName(propertyNames, vote);
    const category = getRulesetCategory(propertyName);

    if (state.allocatedVoteOptions[category] === propertyName) {
      dispatch({ type: 'REMOVE_VOTE', payload: category });
      return;
    }

    dispatch({ type: 'ADD_VOTE', payload: { [category]: propertyName } });
  };

  const isVoteOptionSelected = (voteOption: VoteOption) => {
    if (!voteOption || !voteOption.referenceId || !propertyNames[voteOption.referenceId]) return false;

    const category = getRulesetCategory(propertyNames[voteOption.referenceId].name);

    return state.allocatedVoteOptions[category] === propertyNames[voteOption.referenceId].name;
  };

  const isVoteOptionActive = (voteOption: VoteOption) => {
    if (!voteOption) return false;

    if (bracketType === 'BY_MATCHUP') {
      return voteOption.is_active && voteOption.roundId === activeRound;
    }

    return !!voteOption.referenceId && !!propertyNames[voteOption.referenceId] && voteOption.roundId === activeRound;
  };

  const resetVotes = () => {
    dispatch({ type: 'RESET' });
  };

  const submitVotes = async () => {
    if (!user) {
      return Promise.reject('Must be logged in to vote');
    }

    const response = isPreviewUrl
      ? await mockVoteRequest(state.allocatedVoteOptions)
      : await vote({
          ...user.user,
          action_type: 'vote',
          ...state.allocatedVoteOptions,
        });

    if (response.response_code === VoteAPIResponseCode.VALID) {
      updateUserVoteHistory(response.votestring ?? '');
    }
    dispatch({ type: 'SUBMIT' });

    return response;
  };

  const submitSingleVote = async (voteOption: VoteOption) => {
    if (!user) {
      return Promise.reject('Must be logged in to vote');
    }

    const propertyName = getRulesetPropertyName(propertyNames, voteOption);
    const category = getRulesetCategory(propertyName);

    const response = isPreviewUrl
      ? await mockVoteRequest({ [category]: propertyName })
      : await vote({
          ...user.user,
          action_type: 'vote',
          [category]: propertyName,
        });

    if (response.response_code === VoteAPIResponseCode.VALID) {
      updateUserVoteHistory(response.votestring ?? '');
    }

    return response;
  };

  // Determines if the vote option is eligible to be voted on. Whether a vote option is eligible to be voted on depends on the bracket type
  const isVoteOptionVoteable = (voteOption: VoteOption) => {
    if (!voteOption) return false;

    if (bracketType === 'BY_MATCHUP') {
      return voteOption.is_active && !hasMatchupBeenVotedOn(voteOption);
    }

    return !state.isDirty && !isBracketVoteLimitReached();
  };

  const isBracketVoteLimitReached = () => {
    return (user ? user.votestring.total : 0) >= voteLimit;
  };

  // Checks if the user has already voted on this matchup
  const hasMatchupBeenVotedOn = (voteOption: VoteOption) => {
    if (!voteOption.referenceId || !propertyNames[voteOption.referenceId]) return false;

    const category = getRulesetCategory(propertyNames[voteOption.referenceId].name);

    return Object.keys(state.allocatedVoteOptions).includes(category);
  };

  const ctx: BracketGridContextProps = {
    ...state,
    activeRound,
    totalRounds,
    totalContestants: Math.pow(2, totalRounds) as BracketSize,
    voteOptions,
    winner,
    totalAllocatedVotes: Object.entries(state.allocatedVoteOptions).length,
    totalVotesRequired: Math.pow(2, totalRounds - activeRound),
    displayedRound,
    voteLimit,
    hasReachedVoteLimit: isBracketVoteLimitReached(),
    votesRemaining: user ? voteLimit - user.votestring.total : voteLimit,
    pendingMatchupVote,
    pendingBracketVote,
    resetVotes,
    toggleVote,
    isVoteOptionSelected,
    isVoteOptionActive,
    isVoteOptionVoteable,
    setDisplayedRound,
    submitVotes,
    submitSingleVote,
    setPendingBracketVote,
    setPendingMatchupVote,
  };

  return <BracketGridContext.Provider value={ctx}>{children}</BracketGridContext.Provider>;
}

export function useBracketGrid() {
  const ctx = useContext(BracketGridContext);

  if (!ctx) {
    throw new Error(
      'useBracketGrid hook was called outside of context, wrap your app with BracketGridProvider component'
    );
  }

  return ctx;
}

function getRulesetPropertyName(propertyNames: Record<string, PropertyName>, voteOption: VoteOption) {
  console.assert(voteOption.referenceId, 'ReferenceId missing from voteOption');

  return propertyNames[voteOption.referenceId!].name;
}

/**
 * Parses the property name and returns the "category" name used for voting. The category identifies a unique round and match combination.
 * @param propertyName the ruleset parameter property name
 * @returns category name
 */
function getRulesetCategory(propertyName: string) {
  const [match] = propertyName.match(/r_\d+_m_\d+/) ?? []; // Example property name: "ssp_52868_113324_r_1_m_1_1665011420644_rclgphzprn", where "r_1_m_1" would be the matched string
  console.assert(match, 'Unable to parse property name');

  return match!;
}

/**
 * The app uses the ruleset property names to keep track of which vote options have been allocated for a particular matchup. In preview mode, the ruleset property names are not available until a snapshot has been pushed live
 * so we need to generate mock property names to use in preview mode, in order to simulate the full voting experience.
 */
function generateMockPropertyNames(voteOptions: VoteOption[], activeRound: number) {
  const propertyNames: Record<string, PropertyName> = {};
  const filteredVoteOptions = voteOptions.filter(
    (voteOption) => !!voteOption.referenceId && voteOption.roundId === activeRound
  );
  const size = filteredVoteOptions.length;

  for (let i = 0; i < size; i++) {
    const { name, schema_reference, match_up_id, roundId } = filteredVoteOptions[i];
    propertyNames[i] = {
      name: `r_${roundId}_m_${match_up_id}_${name}`,
      reference: schema_reference!,
      text: name!,
    };
  }

  return keyBy(propertyNames, 'reference');
}

/**
 * Mocks the vote API response for use in preview mode
 */
function mockVoteRequest(payload: any = {}): Promise<UseVoteAPIResponseDTO> {
  return Promise.resolve({
    response_code: VoteAPIResponseCode.VALID,
    votestring: JSON.stringify({
      total: 1,
      ...payload,
    }),
  });
}
